import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-rose-100">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('forgotPassword.checkEmail')}</h1>
          <p className="text-rose-600 mb-6">
            {t('forgotPassword.checkEmailDesc')}
          </p>
          <p className="text-rose-500 text-sm mb-8">
            {t('forgotPassword.linkExpiry')}
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
          >
            {t('nav.backToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back Link */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-800 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.backToLogin')}
        </Link>

        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} alt="SingleSeen" className="h-24 w-auto" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-rose-100">
          <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('forgotPassword.title')}</h1>
          <p className="text-rose-600 mb-8">
            {t('forgotPassword.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-rose-700">{t('forgotPassword.emailLabel')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-rose-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10 bg-white border-rose-200 focus:border-rose-500 h-12"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full h-12 text-lg font-medium"
              data-testid="forgot-submit-btn"
            >
              {loading ? t('common.sending') : t('forgotPassword.sendReset')}
            </Button>
          </form>

          <p className="text-center text-rose-600 mt-6 text-sm">
            {t('forgotPassword.rememberPassword')}{" "}
            <Link to="/login" className="text-rose-700 font-medium hover:underline">
              {t('common.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
